'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useTheme } from '../theme/provider'

export interface Article {
  id: number | string
  title: {
    rendered: string
  }
  excerpt: {
    rendered: string
  }
  date: string
  jetpack_featured_media_url?: string
  [key: string]: any
}

export interface ArticleCardProps {
  article: Article
  index?: number
  getOptimizedImageUrl?: (url: string, width?: number, quality?: number) => string
  getCategoryLabel?: (article: Article, fallbackIndex?: number) => string
  formatDate?: (date: string) => string
  getArticleUrl: (article: Article) => string
  stripHtml?: (html: string) => string
  loading?: 'lazy' | 'eager'
  priority?: boolean
  locale?: string
  /**
   * Override CTA text (defaults to theme translations)
   */
  ctaText?: string
  /**
   * Override category badge (if not using getCategoryLabel)
   */
  category?: string
}

// Helper function to get relative date
function getRelativeDate(dateString: string, locale: string = 'en'): string {
  const date = new Date(dateString)
  const now = new Date()

  const dateStartOfDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  )
  const nowStartOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  )

  const diffInMs = nowStartOfDay.getTime() - dateStartOfDay.getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  const time = date.toLocaleTimeString(
    locale === 'en' ? 'en-US' : locale === 'es' ? 'es-ES' : 'fr-FR',
    {
      hour: '2-digit',
      minute: '2-digit',
    }
  )

  const translations = {
    fr: {
      today: `Aujourd'hui à ${time}`,
      yesterday: `Hier à ${time}`,
      daysAgo: (days: number) => `Il y a ${days} jour${days > 1 ? 's' : ''}`,
    },
    en: {
      today: `Today at ${time}`,
      yesterday: `Yesterday at ${time}`,
      daysAgo: (days: number) => `${days} day${days > 1 ? 's' : ''} ago`,
    },
    es: {
      today: `Hoy a las ${time}`,
      yesterday: `Ayer a las ${time}`,
      daysAgo: (days: number) => `Hace ${days} día${days > 1 ? 's' : ''}`,
    },
  }

  const t = translations[locale as keyof typeof translations] || translations.en

  if (diffInDays <= 0) {
    return t.today
  } else if (diffInDays === 1) {
    return t.yesterday
  } else if (diffInDays < 7) {
    return t.daysAgo(diffInDays)
  } else {
    return date.toLocaleDateString(
      locale === 'en' ? 'en-US' : locale === 'es' ? 'es-ES' : 'fr-FR',
      {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      }
    )
  }
}

// Default stripHtml function
const defaultStripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '').trim()
}

/**
 * ArticleCard Component
 * Theme-able card component for displaying article/post previews
 *
 * Features:
 * - Responsive image with hover zoom
 * - Category badge
 * - Relative date formatting
 * - Hover effects and animations
 * - Theme-customizable colors
 *
 * @example
 * ```tsx
 * <ArticleCard
 *   article={article}
 *   getArticleUrl={(article) => `/news/${article.id}`}
 *   locale="en"
 * />
 * ```
 */
export function ArticleCard({
  article,
  index = 0,
  getOptimizedImageUrl,
  getCategoryLabel,
  formatDate,
  getArticleUrl,
  stripHtml = defaultStripHtml,
  loading = 'lazy',
  priority = false,
  locale = 'en',
  ctaText,
  category,
}: ArticleCardProps) {
  const { theme, translations } = useTheme()

  const primaryColor = theme.colors.primary
  const secondaryColor = theme.colors.secondary || theme.colors.primary
  const imageUrl = article.jetpack_featured_media_url || '/placeholder-image.jpg'
  const optimizedImageUrl = getOptimizedImageUrl
    ? getOptimizedImageUrl(imageUrl, 400, 80)
    : imageUrl

  const categoryLabel =
    category ||
    (getCategoryLabel ? getCategoryLabel(article, index) : 'Article')

  const dateText = formatDate
    ? formatDate(article.date)
    : getRelativeDate(article.date, locale)

  const readMoreText =
    ctaText || translations.articleCard?.readCta || 'Read'

  return (
    <Link href={getArticleUrl(article)} className="group block">
      <article
        className="relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border transform hover:-translate-y-1 h-full flex flex-col"
        style={{
          borderRadius: theme.components?.articleCard?.borderRadius || '0.75rem',
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.border,
        }}
      >
        {/* Image Container */}
        <div
          className="relative overflow-hidden"
          style={{
            height: theme.components?.articleCard?.imageHeight || '14rem',
            backgroundColor: theme.colors.background,
          }}
        >
          <Image
            src={optimizedImageUrl}
            alt={stripHtml(article.title.rendered)}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            quality={80}
            loading={priority ? undefined : loading}
            priority={priority}
            placeholder="blur"
            blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=="
            fetchPriority={index < 3 ? 'high' : 'low'}
          />

          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

          {/* Category Badge */}
          <div className="absolute top-3 left-3">
            <span
              className="inline-flex items-center px-2 py-0.5 text-[9px] font-bold backdrop-blur-md rounded-full shadow-lg border uppercase tracking-wider"
              style={{
                color: '#ffffff',
                backgroundColor: primaryColor,
                borderColor: `${primaryColor}80`,
              }}
            >
              {categoryLabel}
            </span>
          </div>

          {/* Read badge - top right */}
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
            <div
              className="backdrop-blur-md rounded-full px-3 py-1.5 shadow-lg"
              style={{ backgroundColor: `${theme.colors.surface}e6` }}
            >
              <span
                className="text-[10px] font-bold uppercase tracking-wide"
                style={{ color: theme.colors.textSecondary }}
              >
                {readMoreText}
              </span>
            </div>
          </div>

          {/* Hover overlay with themed gradient */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-current/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ color: primaryColor }}
          ></div>
        </div>

        {/* Content Container */}
        <div
          className="flex-1 flex flex-col p-5 overflow-hidden"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface}, ${theme.colors.background}4d)`,
          }}
        >
          {/* Title */}
          <h3
            className="text-base font-bold line-clamp-2 mb-2.5 transition-colors leading-tight min-h-[2.5rem]"
            style={{ color: theme.colors.text }}
          >
            <span className="group-hover:text-current" style={{ color: 'inherit' }}>
              <span className="transition-colors" style={{ color: theme.colors.text }}>
                <span
                  className="transition-colors"
                  style={{ color: 'inherit' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = primaryColor
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme.colors.text || '#000000'
                  }}
                >
                  {stripHtml(article.title.rendered)}
                </span>
              </span>
            </span>
          </h3>

          {/* Excerpt */}
          <p className="text-sm line-clamp-3 mb-4 leading-relaxed" style={{ color: theme.colors.textSecondary }}>
            {stripHtml(article.excerpt.rendered)}
          </p>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Divider */}
          <div
            className="h-px mb-4"
            style={{
              background: `linear-gradient(to right, transparent, ${theme.colors.divider || theme.colors.border}, transparent)`,
            }}
          ></div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            {/* Date */}
            <div className="flex items-center gap-1.5" style={{ color: theme.colors.textSecondary }}>
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: theme.colors.background }}
              >
                <svg
                  className="w-2.5 h-2.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: theme.colors.textSecondary }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-[11px] font-medium">{dateText}</span>
            </div>

            {/* CTA Button */}
            <div className="flex items-center gap-1.5 group-hover:gap-2.5 transition-all duration-300">
              <span
                className="text-xs font-bold uppercase tracking-wide"
                style={{ color: primaryColor }}
              >
                {readMoreText}
              </span>
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-300"
                style={{
                  backgroundColor: primaryColor,
                }}
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center group-hover:bg-current transition-colors duration-300"
                  style={{ backgroundColor: primaryColor }}
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-300"
                    style={{
                      backgroundColor: primaryColor,
                    }}
                  >
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{
                        color: 'white',
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Accent corner */}
        <div
          className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-current/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ color: secondaryColor }}
        ></div>
      </article>
    </Link>
  )
}
